// src/utils/cartSignature.js
// Identity of a cart line for quantity-merging purposes. Two servings of the
// same dish with the SAME selected options are one line with quantity 2 rather
// than two repeated lines (Claire, Jul 2026). Servings customized differently
// stay separate, since each is cooked differently.

const OPTION_KEYS = [
  'proteinOptions',
  'veggieOptions',
  'starchOptions',
  'sauceOptions',
  'garnishOptions',
];

/**
 * Stable string describing a line's dish, meal type and every active option.
 * @param {object} line
 * @param {object} [options]
 * @param {boolean} [options.includeDeliverySlot] - also key on the delivery
 *   slot, so identical meals on different days stay separate rows (checkout).
 *   The cart drawer leaves this off and keeps them merged.
 */
export function customizationSignature(line, { includeDeliverySlot = false } = {}) {
  if (!line) return '';
  const parts = [String(line.dishId), String(line.mealType), String(line.memberId)];
  OPTION_KEYS.forEach((key) => {
    const activeIds = (line[key] || [])
      .filter((o) => o.isActive)
      .map((o) => o.id)
      .sort();
    parts.push(`${key}:${activeIds.join(',')}`);
  });
  if (includeDeliverySlot) parts.push(`slot:${line.deliverySlot || ''}`);
  return parts.join('|');
}

/** Find an existing line (other than `excludeLineId`) that matches `line` exactly. */
export function findMergeableLine(cart = [], line, excludeLineId = null) {
  const sig = customizationSignature(line);
  return cart.find(
    (c) => c.lineId !== excludeLineId && customizationSignature(c) === sig,
  );
}

/**
 * Groups cart lines by customization signature for display — servings that are
 * identical (same dish, portion and every selected ingredient) are shown
 * consolidated as one row with a combined quantity, rather than as repeated
 * rows (Claire, Jul 2026). The underlying lines stay separate; only the
 * returned grouping is for display.
 * @param {object[]} lines
 * @param {object} [options] - passed through to customizationSignature.
 * @returns {Array<{key: string, lines: object[], quantity: number}>}
 */
export function groupCartLinesBySignature(lines = [], options = {}) {
  const bySignature = new Map();
  lines.forEach((line) => {
    const sig = customizationSignature(line, options);
    if (!bySignature.has(sig)) bySignature.set(sig, []);
    bySignature.get(sig).push(line);
  });
  return [...bySignature.values()].map((grouped) => ({
    key: customizationSignature(grouped[0], options),
    lines: grouped,
    quantity: grouped.reduce((sum, l) => sum + (l.quantity || 1), 0),
  }));
}
